import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import type { User } from '@alltrails/types/User';
import type { ConditionStyleId, DynamicStyleId, StyleId } from '../types/Styles';
import { conditionsStyleIds } from './constants';

export const stackableOverlayIds = [
  'newHeatmap',
  'personalHeatmap',
  ...conditionsStyleIds,
  'publicLands',
  'privateLands',
  'fallFoliage',
  'wildfire',
  'slopeAngle',
  'highDefTerrain'
] as const satisfies readonly DynamicStyleId[];

export type StackableOverlayId = (typeof stackableOverlayIds)[number];

export const maxStackedOverlays = 3;

export const overlayStackingUpsellTrigger: UpsellTrigger = 'Select Additional Map Overlay';

export const hasStackingEntitlement = (user: User | null) => hasFeatureEntitlement(user, 'map_overlays_multi_select');

export const getStackedOverlayLimit = (user: User | null) => (hasStackingEntitlement(user) ? maxStackedOverlays : 1);

export const needsStackingUpsell = (user: User | null, stackedOverlayIds: StackableOverlayId[]) =>
  stackedOverlayIds.length > 0 && !hasStackingEntitlement(user);

export const isStackableOverlay = (styleId: StyleId): styleId is StackableOverlayId => (stackableOverlayIds as readonly StyleId[]).includes(styleId);

export const isConditionStyleId = (styleId: StyleId): styleId is ConditionStyleId => (conditionsStyleIds as StyleId[]).includes(styleId);

export const canAddStackedOverlay = (stackedOverlayIds: StackableOverlayId[]) => stackedOverlayIds.length < maxStackedOverlays;

export const toggleStackedOverlay = (stackedOverlayIds: StackableOverlayId[], styleId: StackableOverlayId) =>
  stackedOverlayIds.includes(styleId) ? stackedOverlayIds.filter(id => id !== styleId) : [...stackedOverlayIds, styleId];

export type OverlayCheckboxState = 'checked' | 'unchecked' | 'unavailable';

export const getOverlayCheckboxState = (stackedOverlayIds: StackableOverlayId[], styleId: StackableOverlayId): OverlayCheckboxState | undefined => {
  if (stackedOverlayIds.length === 0) {
    return undefined;
  }
  if (stackedOverlayIds.includes(styleId)) {
    return 'checked';
  }
  return canAddStackedOverlay(stackedOverlayIds) ? 'unchecked' : 'unavailable';
};
